import React from 'react';
import { type ConnectedProps, connect } from 'react-redux';

import { IsNotCAP } from 'components/CAP/IsNotCAP/IsNotCAP';
import { FormattedAmount } from 'components/FormattedAmount/FormattedAmount';
import { Spinner } from 'components/Spinner/Spinner';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { CAMPUS_FIELD, FF_SHOW_FX_RATE_RC } from 'constants/index';
import { RecurringReceivable } from 'containers/RecurringReceivable/RecurringReceivable';
import { useAccountant } from 'hooks/useAccountant/useAccountant';
import { useFeatureFlags } from 'hooks/useFeatureFlags/useFeatureFlags';
import { ReactComponent as InfoIcon } from 'icons/ico-info-new.svg';
import type { RootState } from 'reducers/types';
import {
  amountFrom,
  amountTo,
  getCurrentCountry,
  getExtraFees,
  getFieldById,
  getFieldValue,
  getHiddenFieldIds,
  getItems,
  getRecipient,
  getSelectedOfferExchangeRate,
  getSelectedOfferFees,
  getTaxes,
  isFetching,
  isPayingFromUS,
  isSelectedOfferLegacyInstallment,
  isSelectedOfferLegacySubscription,
  isSelectedOfferRecurring,
} from 'selectors';
import { formatExchangeRate } from 'utils/formatExchangeRate/formatExchangeRate';
import { useTranslations } from 'utils/translations/useTranslations';

import { Breakdown } from '../Breakdown/Breakdown';
import { Discounts } from '../Discounts/Discounts';
import { AmountFromWithFeesAndTaxes } from './AmountFromWithFeesAndTaxes/AmountFromWithFeesAndTaxes';
import { SelectedOfferFees } from './SelectedOfferFees/SelectedOfferFees';

import './PaymentInformationBreakdown.scss';

type PropsFromRedux = ConnectedProps<typeof connector>;

const PaymentInformationBreakdownComponent = ({
  amountFrom,
  amountTo,
  campus,
  country,
  exchangeRate,
  extraFees,
  fees,
  getFieldValue,
  hiddenFieldIds,
  isFetchingOrder,
  isLegacyInstallment = false,
  isLegacySubscription = false,
  isPayingFromUS = false,
  isRecurring = false,
  items,
  recipient,
  taxes,
}: PropsFromRedux) => {
  const i18n = useTranslations();

  const itemsRows = items.map(item => {
    const itemValue = getFieldValue(item.id);

    return (
      <div key={item.id}>
        <dt>{item.label}</dt>
        <dd>
          <FormattedAmount
            value={itemValue as number}
            currency={amountTo.currency as string}
          />
          <span className="sr-only">,</span>
        </dd>
      </div>
    );
  });

  const { name: countryName } = country;
  const hasMultipleItems = () => items.length > 1;
  const isCampusFieldShowable = () => !hiddenFieldIds.includes(CAMPUS_FIELD);
  const shouldRenderCampus =
    campus && campus.text !== '' && isCampusFieldShowable();
  const hasFeesOrTaxes = taxes.length > 0 || extraFees.length > 0;
  const isDFEnabled = useFeatureFlags(FF_SHOW_FX_RATE_RC);
  const isNonRecurringUSPaymentWithDF =
    isPayingFromUS && isDFEnabled && !isRecurring;

  const accountant = useAccountant({
    amount: { value: (amountFrom?.value ?? 0) - fees.value },
    fees,
    taxes,
    extraFees,
  });

  let inverseExchangeRateValue: number | null = 0;
  if (exchangeRate.value !== 0) {
    inverseExchangeRateValue = 1 / exchangeRate.value;
  }

  const hasValidExchangeRate =
    exchangeRate.value !== 0 || inverseExchangeRateValue !== 0;
  const isDifferentCurrency =
    exchangeRate.currencyFrom.code !== exchangeRate.currencyTo.code;
  const shouldDisplayExchangeRate = hasValidExchangeRate && isDifferentCurrency;

  const shouldDisplayAmountBreakdown =
    accountant.totalFlywireFees > 0 ||
    accountant.totalInternationalFees > 0 ||
    accountant.totalTaxes > 0;

  return (
    <Breakdown
      id="paymentInformation"
      title={i18n.t('paymentInformationBreakdown.paymentInformation')}
    >
      <div
        id="paymentInformation-description"
        className="paymentInformation-description"
      >
        <dl>
          {isLegacyInstallment ? (
            <RecurringReceivable
              render={({ amountTo, currencyFrom, currencyTo, fee }) => (
                <>
                  <div>
                    <dt>{i18n.t('paymentInformationBreakdown.amountFrom')}</dt>
                    <dd>
                      <FormattedAmount value={fee} currency={currencyFrom} />
                    </dd>
                  </div>
                  <div>
                    {hasMultipleItems() && itemsRows}
                    <dt>
                      {i18n.t('paymentInformationBreakdown.amountTo', {
                        recipient: recipient.name as string,
                      })}
                    </dt>
                    <dd>
                      <FormattedAmount.Round
                        value={amountTo}
                        currency={currencyTo}
                      />
                      <span className="sr-only">,</span>
                    </dd>
                  </div>
                </>
              )}
            />
          ) : isLegacySubscription ? (
            <>
              <div>
                <dt>{i18n.t('paymentInformationBreakdown.firstRecurring')}</dt>
                <dd>
                  <FormattedAmount
                    value={amountFrom.value as number}
                    currency={amountFrom.currency as string}
                  />
                  <span className="sr-only">,</span>
                </dd>
              </div>
              <div>
                <dt>
                  {i18n.t('paymentInformationBreakdown.amountTo', {
                    recipient: recipient.name as string,
                  })}
                </dt>
                <dd>
                  <FormattedAmount
                    value={amountTo.value}
                    currency={amountTo.currency as string}
                  />
                  <span className="sr-only">,</span>
                </dd>
              </div>
            </>
          ) : isFetchingOrder() ? (
            <Spinner className="paymentInformation-spinner" />
          ) : (
            <>
              {isNonRecurringUSPaymentWithDF ? (
                <>
                  {shouldDisplayAmountBreakdown && (
                    <>
                      <div
                        className="m-0 flex items-center justify-between"
                        data-ss-hidden
                      >
                        <dt className="text-sm">
                          {i18n.t('paymentInformationBreakdown.originalAmount')}
                        </dt>
                        <dd className="!m-0 text-base !font-normal">
                          <FormattedAmount
                            value={accountant.total}
                            currency={amountFrom.currency as string}
                          />
                          <span className="sr-only">,</span>
                        </dd>
                      </div>
                      <IsNotCAP>
                        {accountant.totalFlywireFees > 0 && (
                          <div className="mt-1 flex items-center justify-between">
                            <dt className="text-sm">
                              {i18n.t('paymentInformationBreakdown.fees')}
                            </dt>
                            <dd className="!m-0 text-base !font-normal">
                              <FormattedAmount
                                value={accountant.totalFlywireFees}
                                currency={amountFrom.currency as string}
                              />
                              <span className="sr-only">,</span>
                            </dd>
                          </div>
                        )}
                        {accountant.totalInternationalFees > 0 && (
                          <div className="mt-1 flex items-center justify-between">
                            <dt className="text-sm">
                              {i18n.t(
                                'paymentInformationBreakdown.internationalFees',
                              )}
                            </dt>
                            <dd className="!m-0 text-base !font-normal">
                              <FormattedAmount
                                value={accountant.totalInternationalFees}
                                currency={amountFrom.currency as string}
                              />
                              <span className="sr-only">,</span>
                            </dd>
                          </div>
                        )}
                      </IsNotCAP>
                      {accountant.totalTaxes > 0 && (
                        <div className="mt-1 flex items-center justify-between">
                          <dt className="text-sm">
                            {i18n.t('paymentInformationBreakdown.tax')}
                          </dt>
                          <dd className="!m-0 text-base !font-normal">
                            <FormattedAmount
                              value={accountant.totalTaxes}
                              currency={amountFrom.currency as string}
                            />
                            <span className="sr-only">,</span>
                          </dd>
                        </div>
                      )}
                      <div className="my-4 border-t border-gray-500" />
                    </>
                  )}
                  <div
                    className="mt-1 flex items-center justify-between"
                    data-ss-hidden
                  >
                    <dt className="text-sm">
                      {i18n.t('paymentInformationBreakdown.total_send')}
                    </dt>
                    <dd className="!m-0 !text-xl font-semibold">
                      <FormattedAmount
                        value={accountant.totalWithTaxesAndFees}
                        currency={amountFrom.currency as string}
                      />
                      <span className="sr-only">,</span>
                    </dd>
                  </div>

                  {shouldDisplayExchangeRate && (
                    <div
                      className="mt-4 flex items-center justify-between border-2"
                      data-testid="exchangeRate"
                    >
                      <dt className="flex w-1/3 items-center gap-1.5 self-start text-sm">
                        {i18n.t('paymentInformationBreakdown.rate')}{' '}
                        <Tooltip
                          defaultMessage={i18n.t(
                            'paymentInformationBreakdown.exchante_rate',
                          )}
                          data-testid="exchante-rate-tooltip"
                          className="hidden bg-white md:block"
                        >
                          <InfoIcon height="14" width="14" aria-hidden="true" />
                        </Tooltip>
                      </dt>

                      <dd className="!m-0 w-2/3 self-end text-right !font-normal">
                        {exchangeRate.value !== 0 && (
                          <span className="block text-base">
                            1 {exchangeRate.currencyFrom.code} ={' '}
                            {formatExchangeRate(exchangeRate.value)}{' '}
                            {exchangeRate.currencyTo.code}
                          </span>
                        )}
                        {inverseExchangeRateValue !== 0 && (
                          <span className="block text-base">
                            1 {exchangeRate.currencyTo.code} ={' '}
                            {formatExchangeRate(inverseExchangeRateValue)}{' '}
                            {exchangeRate.currencyFrom.code}
                          </span>
                        )}
                        <span className="sr-only">,</span>
                      </dd>
                    </div>
                  )}

                  {hasMultipleItems() && (
                    <>
                      <div className="mt-6" />
                      {items.map(item => {
                        const itemValue = getFieldValue(item.id);
                        return (
                          <div
                            className="mt-1 flex items-center justify-between"
                            key={item.id}
                          >
                            <dt className="text-sm">{item.label}</dt>
                            <dd className="!m-0 text-base !font-normal">
                              <FormattedAmount
                                value={itemValue as number}
                                currency={amountTo.currency as string}
                              />
                              <span className="sr-only">,</span>
                            </dd>
                          </div>
                        );
                      })}
                      <div className="my-4 border-t border-gray-500" />
                    </>
                  )}
                  <div className="mt-4 flex items-center justify-between gap-10">
                    <dt className="flex-1 text-sm">
                      {i18n.t('paymentInformationBreakdown.amountTo.short', {
                        recipient: recipient.name as string,
                      })}
                    </dt>
                    <dd className="!m-0 self-start !text-xl font-semibold">
                      <FormattedAmount
                        value={amountTo.value}
                        currency={amountTo.currency as string}
                      />
                    </dd>
                    <span className="sr-only">,</span>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <dt>
                      {isRecurring
                        ? i18n.t(
                            'paymentInformationBreakdown.recurring.amountFrom',
                          )
                        : i18n.t('paymentInformationBreakdown.amountFrom')}
                    </dt>
                    <dd data-testid="amount-from" data-ss-hidden>
                      <>
                        {hasFeesOrTaxes ? (
                          <AmountFromWithFeesAndTaxes
                            amountFrom={amountFrom}
                            taxes={taxes}
                            extraFees={extraFees}
                          />
                        ) : (
                          <FormattedAmount
                            value={amountFrom.value as number}
                            currency={amountFrom.currency as string}
                          />
                        )}

                        <IsNotCAP>
                          <SelectedOfferFees />
                        </IsNotCAP>

                        <span className="sr-only">,</span>
                      </>
                    </dd>
                  </div>

                  <div>
                    <dt>
                      {i18n.t('paymentInformationBreakdown.amountTo', {
                        recipient: recipient.name as string,
                      })}
                    </dt>
                    <dd>
                      <FormattedAmount
                        value={amountTo.value}
                        currency={amountTo.currency as string}
                      />
                      <span className="sr-only">,</span>
                    </dd>
                  </div>
                </>
              )}
            </>
          )}
          <Discounts className={isNonRecurringUSPaymentWithDF ? 'mt-6' : ''} />
          <div>
            <dt>{i18n.t('paymentInformationBreakdown.country.label')}</dt>
            <dd data-testid="sender-country" data-hj-suppress>
              {countryName}
            </dd>
          </div>
          {shouldRenderCampus && (
            <div>
              <dt data-testid="campusLabel">
                {i18n.t('paymentInformationBreakdown.campus.label')}
              </dt>
              <dd data-hj-suppress data-testid="campus">
                {campus.text}
              </dd>
            </div>
          )}
        </dl>
        {hasMultipleItems() && !isNonRecurringUSPaymentWithDF && (
          <dl>{itemsRows}</dl>
        )}
      </div>
    </Breakdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  amountFrom: amountFrom(state),
  amountTo: amountTo(state),
  campus: getFieldById(state, 'campus'),
  country: getCurrentCountry(state),
  exchangeRate: getSelectedOfferExchangeRate(state),
  extraFees: getExtraFees(state),
  fees: getSelectedOfferFees(state),
  getFieldValue: (name: string) => getFieldValue(state, name),
  hiddenFieldIds: getHiddenFieldIds(state),
  isFetchingOrder: () => isFetching(state, 'order'),
  isLegacyInstallment: isSelectedOfferLegacyInstallment(state),
  isLegacySubscription: isSelectedOfferLegacySubscription(state),
  isPayingFromUS: isPayingFromUS(state),
  isRecurring: isSelectedOfferRecurring(state),
  items: getItems(state),
  recipient: getRecipient(state),
  taxes: getTaxes(state),
});

const connector = connect(mapStateToProps);
const PaymentInformationBreakdown = connector(
  PaymentInformationBreakdownComponent,
);

export { PaymentInformationBreakdown };
